// ============================================================================
// POSTING REPOSITORY
// ARQUIVO: src/services/PostingRepository.js
// RESPONSABILIDADE: CRUD de lançamentos financeiros pessoais (postings, templates, settings).
// Criado: Fase 6.3 — mover Firestore direto de usePostings.js para repositório.
// ============================================================================

import {
  collection,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
  deleteDoc,
  doc,
  writeBatch,
  where,
  setDoc,
  getDoc,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import { db } from "../shared/api/firebase/config";
import { FIREBASE_CONFIG } from "../config/Config";
import {
  createPair,
  createSingle,
} from "../features/finance/personal/services/postingService.js";

const APP_ID = FIREBASE_CONFIG?.projectId || "portalrda-f312d";

const PATH_POSTINGS = [
  "artifacts",
  APP_ID,
  "public",
  "data",
  "personal_postings",
];
const PATH_TEMPLATES = [
  "artifacts",
  APP_ID,
  "public",
  "data",
  "personal_recurrence_templates",
];
const PATH_SETTINGS = [
  "artifacts",
  APP_ID,
  "public",
  "data",
  "personal_finance_settings",
  "config",
];

/** Carrega configurações (categorias e saldo inicial). */
export async function loadPersonalFinanceSettings() {
  try {
    if (!db) return null;
    const docRef = doc(db, ...PATH_SETTINGS);
    const snap = await getDoc(docRef);
    return snap.exists() ? snap.data() : null;
  } catch (e) {
    console.log("PostingRepository: settings padrão");
    return null;
  }
}

/** Carrega templates de recorrência. */
export async function loadTemplates() {
  const ref = collection(db, ...PATH_TEMPLATES);
  const snap = await getDocs(query(ref));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Carrega lançamentos do mês. */
export async function loadPostingsByMonth(year, month, filterMode = "DATE") {
  const startOfMonth = `${year}-${String(month + 1).padStart(2, "0")}-01`;
  const endOfMonth = `${year}-${String(month + 2).padStart(2, "0")}-00`; // dia 0 do próximo mês = último dia deste
  const queryField = filterMode === "COMPETENCE" ? "competenceDate" : "date";

  const ref = collection(db, ...PATH_POSTINGS);
  const q = query(
    ref,
    where(queryField, ">=", startOfMonth),
    where(queryField, "<=", endOfMonth),
    orderBy(queryField, "asc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Carrega lançamentos do ano. */
export async function loadPostingsByYear(year, yearViewMode = "ALL") {
  const startOfYear = `${year}-01-01`;
  const endOfYear = `${year}-12-31`;
  const ref = collection(db, ...PATH_POSTINGS);
  const q = query(
    ref,
    where("date", ">=", startOfYear),
    where("date", "<=", endOfYear),
  );
  const snap = await getDocs(q);
  const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  return yearViewMode === "PAID_ONLY"
    ? data.filter((d) => d.status === "PAID")
    : data;
}

/** Atualiza um lançamento existente. */
export async function updatePosting(id, formData) {
  const docRef = doc(db, ...PATH_POSTINGS, id);
  const cleanAmount = Math.round(Number(formData.amount) * 100) / 100;
  const targetStatus = formData.isPaid ? "PAID" : formData.status || "PENDING";
  const today = new Date().toISOString().split("T")[0];
  if (targetStatus === "PAID" && formData.date > today) {
    throw new Error(
      "INV-4: Não é possível marcar como PAGO um lançamento com data futura.",
    );
  }
  const { id: _id, type: _type, ...rest } = formData;
  const finalData = {
    ...rest,
    group: _type,
    amount: cleanAmount,
    status: targetStatus,
    settlementDate:
      targetStatus === "PAID" ? formData.settlementDate || today : null,
    vinculo: formData.vinculo || null,
  };
  await updateDoc(docRef, finalData);
}

/** Cria template de recorrência. */
export async function createTemplate(formData) {
  const batch = writeBatch(db);
  const templatesRef = collection(db, ...PATH_TEMPLATES);
  const newTplRef = doc(templatesRef);
  const cleanAmount = Math.round(Number(formData.amount) * 100) / 100;
  batch.set(newTplRef, {
    description: formData.description,
    amount: cleanAmount,
    group: formData.type,
    category: formData.category || formData.subCategory || formData.type,
    sudNivel1: formData.sudNivel1 || null,
    sudNivel2: formData.sudNivel2 || null,
    sudNivel3: formData.sudNivel3 || null,
    sudVars: formData.sudVars || null,
    anchorDay:
      Number(formData.anchorDay) ||
      new Date(`${formData.date}T12:00:00`).getDate(),
    active: true,
    account: formData.account,
    createdAt: serverTimestamp(),
  });
  await batch.commit();
}

/** Cria lançamento(s) (único ou parcelado — recorrente mensal usa createTemplate). */
export async function createPosting(formData, mode) {
  const totalAmount = Math.round(Number(formData.amount) * 100) / 100;
  // mode já vem calculado pelo modal (SINGLE/INSTALLMENT) — nunca expandir
  // SINGLE em N meses aqui. Isso já causou duplicação silenciosa de
  // lançamentos "Único" em 12 cópias mensais (recorrencia/repeatCount são
  // apenas resíduos do estado padrão do formulário, não uma intenção real
  // de recorrência quando mode === "SINGLE"). Achado e corrigido 17/09/2026.
  // Parcelado pode começar de uma parcela já em andamento (ex: registrar
  // agora uma compra que já está na 5ª de 10 parcelas) — usa "Parcela Nº"
  // como ponto de partida em vez de sempre recomeçar do 1. Achado real
  // 18/09/2026: o sistema ignorava esse campo e sempre gerava 1/N, 2/N...
  const parcelaTotalCount =
    mode === "INSTALLMENT" ? formData.repeatCount : 1;
  const parcelaStart =
    mode === "INSTALLMENT" ? parseInt(formData.parcelaNumero) || 1 : 1;
  // Achado real 18/09/2026: o campo "Repetições" do modo Recorrente sempre
  // existiu na tela e o titular sempre pôde digitar qualquer número nele —
  // mas nada usava esse valor pra gerar lançamentos de verdade nos meses
  // seguintes (só o mês da criação virava lançamento real). Agora "Recorrente"
  // gera de fato `repeatCount` lançamentos, no intervalo escolhido
  // (Mensal/Bimestral/Trimestral/Semestral/Anual), não só um.
  const RECORRENCIA_STEP_MESES = {
    Mensal: 1,
    Bimestral: 2,
    Trimestral: 3,
    Semestral: 6,
    Anual: 12,
  };
  const stepMeses =
    mode === "RECURRING"
      ? RECORRENCIA_STEP_MESES[formData.recorrencia] || 1
      : 1;
  const count =
    mode === "INSTALLMENT"
      ? Math.max(parcelaTotalCount - parcelaStart + 1, 1)
      : mode === "RECURRING"
        ? Math.max(parseInt(formData.repeatCount) || 1, 1)
        : 1;
  // Achado real 18/09/2026: "Valor Real" era tratado como o TOTAL da compra
  // inteira e dividido pelo nº de parcelas — mas ao editar/duplicar uma
  // parcela já existente (que já mostra o valor daquela parcela, não o
  // total), o mesmo valor sendo digitado de novo era dividido OUTRA vez
  // (ex: R$54 virava R$5,40). "Valor Real" agora é sempre o valor desta
  // parcela específica, igual em todo o resto do formulário — sem divisão.
  const valuePerInstallment = totalAmount;

  for (let i = 0; i < count; i++) {
    const parcelaAtual = parcelaStart + i;
    const monthOffset = mode === "RECURRING" ? i * stepMeses : i;
    const dueDate = new Date(formData.date);
    dueDate.setMonth(dueDate.getMonth() + monthOffset);

    let compDateStr = formData.competenceDate
      ? `${formData.competenceDate}-01`
      : dueDate.toISOString().split("T")[0];
    if (mode === "INSTALLMENT" || mode === "RECURRING") {
      const compDate = new Date(formData.date);
      compDate.setMonth(compDate.getMonth() + monthOffset);
      compDateStr = compDate.toISOString().split("T")[0];
    }

    // Achado real 18/09/2026: o número da parcela já aparece na etiqueta
    // TIPO ("Parcelado 5/10") — repetir "(5/10)" também na descrição é
    // redundante. A descrição fica limpa; só a etiqueta mostra o número.
    let desc = formData.description;
    if (mode === "RECURRING" && count > 1 && i > 0) {
      const monthName = dueDate.toLocaleDateString("pt-BR", {
        month: "long",
      });
      desc += ` (${monthName}/${dueDate.getFullYear()})`;
    }

    const isPaid = formData.isPaid && i === 0;
    const parcelaAuto =
      mode === "INSTALLMENT"
        ? `${parcelaAtual}/${parcelaTotalCount}`
        : formData.parcela || null;

    const draft = {
      description: desc,
      value: valuePerInstallment,
      date: dueDate.toISOString().split("T")[0],
      competenceDate: compDateStr,
      settlementDate: isPaid ? new Date().toISOString().split("T")[0] : null,
      recorrencia: formData.recorrencia || "Única",
      parcela: parcelaAuto,
      group: formData.type,
      category: formData.category || formData.subCategory || formData.type,
      account: formData.account,
      status: isPaid ? "PAID" : "PENDING",
      // Campos propagados do formData (SSOT — Firestore armazena tudo)
      vinculo: formData.vinculo || null,
      juros: formData.juros ?? null,
      desconto: formData.desconto ?? null,
      acrescimo: formData.acrescimo ?? null,
      tarifa: formData.tarifa ?? null,
      natureza: formData.natureza || null,
      templateId: formData.templateId || null,
      descOut: formData.descOut || null,
      numeroDocumento: formData.numeroDocumento || null,
      dataEmissao: formData.dataEmissao || null,
      paidBy: formData.paidBy || null,
      reimbursable: formData.reimbursable || false,
      needsSettlement: formData.needsSettlement || false,
      modoLancamento: formData.modoLancamento || null,
      sudNivel1: formData.sudNivel1 || null,
      sudNivel2: formData.sudNivel2 || null,
      sudNivel3: formData.sudNivel3 || null,
      sudVars: formData.sudVars || null,
    };

    // createSingle como padrão (Rota A). createPair só com forcePair explícito.
    if (
      formData.type === "RECEITAS" ||
      formData.forceSingle ||
      !formData.forcePair
    ) {
      await createSingle(draft);
    } else {
      await createPair(draft);
    }
  }
}

/**
 * Cria um lançamento filho (para recorrência futura) sem race condition.
 * Usa createSingle diretamente. Adiciona recurrenceGroupId para rastrear árvore familiar.
 */
export async function createChildPosting(parent, parentId) {
  const totalAmount = Math.round(Number(parent.amount) * 100) / 100;
  const draft = {
    description: parent.description,
    value: totalAmount,
    date: parent.date,
    competenceDate: parent.competenceDate || parent.date,
    settlementDate: null,
    recorrencia: parent.recorrencia || "Única",
    parcela: null,
    group: parent.type,
    category: parent.category || parent.subCategory || parent.type,
    account: parent.account,
    status: "PENDING",
    vinculo: parent.vinculo || null,
    recurrenceGroupId: parentId,
    parentPostingId: parentId,
  };
  await createSingle(draft);
}

/** Deleta lançamento ou template. */
export async function deletePosting(id, isTemplate = false) {
  const ref = isTemplate
    ? doc(db, ...PATH_TEMPLATES, id)
    : doc(db, ...PATH_POSTINGS, id);
  await deleteDoc(ref);
}

/** Salva categorias. */
export async function saveCategories(newCats) {
  const docRef = doc(db, ...PATH_SETTINGS);
  await setDoc(docRef, { categories: newCats }, { merge: true });
}

/** Salva saldo inicial. */
export async function saveInitialBalance(newBalance) {
  const docRef = doc(db, ...PATH_SETTINGS);
  await setDoc(docRef, { initialBalance: Number(newBalance) }, { merge: true });
}

// ============================================================================
// TAXONOMIA (SSOT no Firestore) — Grupo/Subcategoria/Natureza/SUD
// Fonte única de verdade em personal_finance_settings/config, lida ao vivo
// (nunca via arquivo estático). categories.js/templates.js viram só seed
// inicial de referência, não a fonte real de dados a partir daqui.
// ============================================================================

/** Adiciona uma nova subcategoria a um grupo existente (sem duplicar — usar
 * TaxonomyCombobox para a checagem normalizada antes de chamar isto). */
export async function addSubcategory(group, value) {
  const docRef = doc(db, ...PATH_SETTINGS);
  await setDoc(
    docRef,
    { categories: { [group]: { subs: arrayUnion(value) } } },
    { merge: true },
  );
}

/** Cria um grupo novo (categoria de topo) com valores padrão. */
export async function addGroup(key, label) {
  const docRef = doc(db, ...PATH_SETTINGS);
  await setDoc(
    docRef,
    {
      categories: {
        [key]: {
          label: label || key,
          color: "text-gray-700 bg-gray-50 border-gray-200",
          nature: "EXPENSE",
          subs: [],
          budget: 0,
        },
      },
    },
    { merge: true },
  );
}

/** Adiciona um valor novo a um nível do SUD (1, 2 ou 3). */
export async function addSudValue(nivel, value) {
  const docRef = doc(db, ...PATH_SETTINGS);
  const field = `nivel${nivel}`;
  await setDoc(
    docRef,
    { sud: { [field]: arrayUnion(value) } },
    { merge: true },
  );
}

/** Adiciona um valor novo de Natureza. */
export async function addNatureza(value) {
  const docRef = doc(db, ...PATH_SETTINGS);
  await setDoc(docRef, { naturezas: arrayUnion(value) }, { merge: true });
}

/** Salva label personalizado de template (↻ persistente). */
export async function saveTemplateLabel(templateId, newLabel, sudInfo) {
  const batch = writeBatch(db);
  const ref = doc(db, ...PATH_TEMPLATES, templateId);
  batch.set(
    ref,
    {
      label: newLabel,
      sudNivel1: sudInfo?.n1 || null,
      sudNivel2: sudInfo?.n2 || null,
      sudNivel3: sudInfo?.n3 || null,
      updatedAt: new Date().toISOString(),
    },
    { merge: true },
  );
  await batch.commit();
}

/** Vincula ID do Bling a um lançamento. */
export async function linkBlingId(postingId, blingId) {
  const ref = doc(db, ...PATH_POSTINGS, postingId);
  await updateDoc(ref, {
    bling_ids: {
      contaId: Number(blingId),
      vinculadoEm: new Date().toISOString(),
    },
  });
}

/** Marca lançamento como pago. */
export async function markPostingPaid(postingId, settlementDate) {
  const effectiveDate =
    settlementDate || new Date().toISOString().split("T")[0];
  const docRef = doc(db, ...PATH_POSTINGS, postingId);
  await updateDoc(docRef, { status: "PAID", settlementDate: effectiveDate });
}

/** Art. 188-G — Migração background: isPaid:true → status:'PAID'. */
export async function migrateLegacyIsPaid() {
  const ref = collection(db, ...PATH_POSTINGS);
  const snap = await getDocs(query(ref, where("isPaid", "==", true)));
  const stale = snap.docs.filter(
    (d) => d.data().status !== "PAID" && d.data().status !== "CANCELLED",
  );
  if (stale.length === 0) return 0;
  const batch = writeBatch(db);
  stale.forEach((d) => {
    const data = d.data();
    batch.update(doc(db, ...PATH_POSTINGS, d.id), {
      status: "PAID",
      settlementDate: data.settlementDate || data.date,
    });
  });
  await batch.commit();
  return stale.length;
}

/** Art. 187 — Auto-cancelamento Unimed. */
export async function cancelUnimedPostings() {
  const ref = collection(db, ...PATH_POSTINGS);
  const snap = await getDocs(query(ref, where("group", "==", "SAUDE")));
  const targets = snap.docs.filter((d) => {
    const desc = String(d.data().description || "").toLowerCase();
    return desc.includes("unimed") && d.data().status !== "CANCELLED";
  });
  for (const t of targets) {
    await updateDoc(doc(db, ...PATH_POSTINGS, t.id), {
      status: "CANCELLED",
      cancellationReason:
        "Art. 187: plano empresarial Recta Scripta = despesa PJ, nao PF (2026-04-30)",
      cancelledAt: new Date().toISOString(),
    });
  }
  return targets.length;
}
